import React, { useContext } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import { Container } from './components/Container';
import { use100vh } from 'react-div-100vh';

import { useSession } from 'store/session';
import { useTranslation } from 'react-i18next';
import { translations } from 'translations';
import { ROUTES } from './helpers';
import { WLContext } from '@contexts';
import { AuthLogo } from './components/AuthLogo';


import { LegalNotice } from './components/LegalNotice';
import { AuthPageContext } from './context';

const AuthPagesContent: React.FC<{
  color: string;
  loginBackground?: string;
  height: number | null;
}> = ({ color, loginBackground, height }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [footerAction, setFooterAction] = React.useState<React.ReactNode>(null);
  const [socialLogin, setSocialLogin] = React.useState<React.ReactNode>(null);
  const [logoContent, setLogoContent] = React.useState<React.ReactNode>(null);
  const [headerTitleContent, setHeaderTitleContent] = React.useState<React.ReactNode>(null);

  // Reset slots on navigation
  React.useEffect(() => {
    setFooterAction(null);
    setSocialLogin(null);
    setHeaderTitleContent(null);
  }, [location.pathname]);

  const setFooterActionStable = React.useCallback((content: React.ReactNode) => {
    setFooterAction(prev => prev === content ? prev : content);
  }, []);

  const setSocialLoginStable = React.useCallback((content: React.ReactNode) => {
    setSocialLogin(prev => prev === content ? prev : content);
  }, []);

  const setLogoStable = React.useCallback((content: React.ReactNode) => {
    setLogoContent(prev => prev === content ? prev : content);
  }, []);

  const setHeaderTitleStable = React.useCallback((content: React.ReactNode) => {
    setHeaderTitleContent(prev => prev === content ? prev : content);
  }, []);

  const contextValue = React.useMemo(() => ({ 
    setFooterAction: setFooterActionStable,
    setSocialLogin: setSocialLoginStable,
    setLogo: setLogoStable,
    setHeaderTitle: setHeaderTitleStable
  }), [setFooterActionStable, setSocialLoginStable, setLogoStable, setHeaderTitleStable]);

  const pageTitle = React.useMemo(() => {
    if (location.pathname.includes('/signup')) {
      return t(translations.AUTH_PAGES.SIGNUP_TITLE);
    }
    
    if (location.pathname.includes('/confirmation')) {
      return t('common.verifyIdentity', 'Verifica tu identidad');
    }

    return t(translations.AUTH_PAGES.LOGIN_TITLE);
  }, [location.pathname, t]);

  const backgroundStyle = loginBackground 
    ? {
        backgroundImage: `url(${loginBackground})`,
        backgroundColor: color,
      }
    : {
        backgroundColor: color || '#6344FF',
        backgroundImage: `
          radial-gradient(at 0% 0%, hsla(253,16%,7%,1) 0, transparent 50%), 
          radial-gradient(at 50% 0%, ${color}aa 0, transparent 50%), 
          radial-gradient(at 100% 0%, hsla(339,49%,30%,1) 0, transparent 50%), 
          radial-gradient(at 0% 100%, ${color} 0, transparent 50%), 
          radial-gradient(at 50% 100%, hsla(339,49%,30%,1) 0, transparent 50%), 
          radial-gradient(at 100% 100%, hsla(253,16%,7%,1) 0, transparent 50%)
        `,
      };

  return (
    <AuthPageContext.Provider value={contextValue}>
      <div className="flex flex-col lg:flex-row w-full overflow-y-auto lg:overflow-hidden bg-white" style={{ minHeight: `${height}px` }}>
        {/* Decorative Side Panel / Mobile Header */}
        <div 
          className="flex lg:w-1/2 flex-col justify-between p-10 lg:p-16 relative min-h-[30vh] lg:min-h-0"
          style={{
            ...backgroundStyle,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="z-20">
            <img alt="Powered by Cincel" src="https://api.cincel.digital/v3/assets/logo.light.png" className="h-6 lg:h-8 w-auto opacity-30" />
          </div>
          
          <div className="z-20 flex flex-col gap-4 lg:gap-8 mb-8 lg:mb-0">
            <div className="max-w-md">
              <h1 className="text-3xl lg:text-4xl xl:text-4xl font-bold text-white leading-[1.1] tracking-tight whitespace-nowrap">
                {pageTitle}
              </h1>
            </div>

            <div className="max-w">
              <p className="text-[9px] lg:text-xs text-white/90 font-medium xl:tracking-widest opacity-70 uppercase leading-relaxed whitespace-nowrap">
                {t(translations.COMMON.CINCEL_TITLE)}
              </p>
            </div>
          </div>

          <div className="absolute inset-0 bg-black/10 z-10 pointer-events-none" />
        </div>

        {/* Main Content Area */}
        <Container className="w-full lg:w-1/2 flex-1 flex flex-col items-center -mt-10 lg:mt-0 rounded-t-[2.5rem] lg:rounded-none z-20 relative p-8 lg:px-12 lg:py-16 bg-white">
          <div className="mb-10 lg:mb-12">
            {logoContent || <AuthLogo isStatic />}
          </div>

          {headerTitleContent}

          <div className="flex-1 flex flex-col items-center justify-center w-full">
            <main className="w-full max-w-[440px] flex flex-col items-center overflow-x-hidden">
              <div className="w-full">
                <Switch>
                  {ROUTES.map(({ element: Element, path }) => (
                    <Route key={path} path={path} component={Element as any} />
                  ))}
                </Switch>
              </div>
            </main>
          </div>
          
          <div className="w-full flex flex-col items-center gap-6 mt-12 bg-white">
            <div className="w-full flex flex-col items-center gap-4">
              {socialLogin}
            </div>
            {footerAction}
            <LegalNotice />
          </div>
        </Container>
      </div>
    </AuthPageContext.Provider>
  );
};

const AuthPages: React.FC = (): JSX.Element | null => {
  const { isFetching, logo, color, loginBackground } = useContext(WLContext);
  const { isLoggedIn } = useSession();
  const height = use100vh();

  if (isLoggedIn) return <Redirect to="/" />;
  if (isFetching) return null;

  return (
    <AuthPagesContent 
      color={color} 
      loginBackground={loginBackground} 
      height={height} 
    />
  );
};

export default AuthPages;
